function slugifyHeading(text) {
  return text
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/['’"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

if (!customElements.get('gl-article')) {
  customElements.define(
    'gl-article',
    class GlArticle extends HTMLElement {
      connectedCallback() {
        this.content = this.querySelector('[data-article-content]');
        this.toc = this.querySelector('[data-article-toc]');
        this.tocNav = this.querySelector('[data-article-toc-nav]');
        this.headings = [];

        this.promoteBoldHeadings();
        this.initHeadings();
        this.initVideos();
        this.initShare();
        this.initTocObserver();
        this.initRelatedProducts();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      paragraphHeadingText(paragraph) {
        const clone = paragraph.cloneNode(true);
        clone.querySelectorAll('br, sup, .superscript').forEach((node) => node.remove());
        return clone.textContent.replace(/\s+/g, ' ').trim();
      }

      promoteBoldHeadings() {
        if (!this.content) return;

        Array.from(this.content.querySelectorAll('p')).forEach((paragraph) => {
          if (paragraph.closest('li')) return;
          if (paragraph.querySelector('img, video, iframe')) return;

          const text = this.paragraphHeadingText(paragraph);
          if (!text || text.length > 110) return;

          const bold = paragraph.querySelector('b, strong');
          if (!bold) return;

          const boldText = bold.textContent.replace(/\s+/g, ' ').trim();
          if (!boldText || boldText !== text) return;

          const heading = document.createElement('h2');
          heading.innerHTML = paragraph.innerHTML;
          paragraph.replaceWith(heading);
        });
      }

      initHeadings() {
        if (!this.content) return;

        const usedIds = new Set();
        const headingNodes = Array.from(this.content.querySelectorAll('h2, h3')).filter(
          (el) => el.textContent.trim().length > 0
        );

        headingNodes.forEach((heading) => {
          if (!heading.id) {
            let id = slugifyHeading(heading.textContent);
            if (!id) return;
            let uniqueId = id;
            let i = 2;
            while (usedIds.has(uniqueId) || document.getElementById(uniqueId)) {
              uniqueId = `${id}-${i}`;
              i += 1;
            }
            heading.id = uniqueId;
          }
          usedIds.add(heading.id);

          heading.classList.add('gl-article__heading');
          const headingText = heading.textContent.replace(/\s+/g, ' ').trim();

          if (!heading.querySelector('.gl-article__heading-link')) {
            const textWrap = document.createElement('span');
            while (heading.firstChild) textWrap.appendChild(heading.firstChild);

            const anchor = document.createElement('a');
            anchor.href = `#${heading.id}`;
            anchor.className = 'gl-article__heading-link';
            anchor.setAttribute('aria-label', 'Link to this section');
            anchor.innerHTML =
              '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';

            heading.appendChild(textWrap);
            heading.appendChild(anchor);
          }

          this.headings.push({
            id: heading.id,
            text: headingText,
            level: Number(heading.tagName.replace('H', '')),
            el: heading,
          });
        });

        this.renderToc();
      }

      renderToc() {
        if (!this.toc || !this.tocNav) return;

        if (this.headings.length === 0) {
          this.toc.hidden = true;
          return;
        }

        this.toc.hidden = false;
        this.tocNav.replaceChildren();
        this.headings.forEach((heading) => {
          const link = document.createElement('a');
          link.href = `#${heading.id}`;
          link.className = `gl-article__toc-link${heading.level === 3 ? ' gl-article__toc-link--sub' : ''}`;
          link.dataset.tocId = heading.id;
          link.textContent = heading.text;
          this.tocNav.appendChild(link);
        });
      }

      initTocObserver() {
        if (!this.headings.length || !this.tocNav) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.setActiveHeading(entry.target.id);
              }
            });
          },
          { rootMargin: '-100px 0% -70% 0%' }
        );

        this.headings.forEach((heading) => this.observer.observe(heading.el));
      }

      setActiveHeading(id) {
        this.tocNav.querySelectorAll('.gl-article__toc-link').forEach((link) => {
          link.classList.toggle('is-active', link.dataset.tocId === id);
        });

        const activeLink = this.tocNav.querySelector(`[data-toc-id="${id}"]`);
        if (!activeLink) return;

        const rect = this.tocNav.getBoundingClientRect();
        const visible = rect.top < window.innerHeight && rect.bottom >= 0;
        if (visible) {
          activeLink.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      initVideos() {
        if (!this.content) return;

        this.content.querySelectorAll('iframe').forEach((iframe) => {
          if (iframe.closest('.gl-article__video')) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'gl-article__video gl-article__video--embed';
          iframe.parentNode.insertBefore(wrapper, iframe);
          wrapper.appendChild(iframe);
        });

        this.content.querySelectorAll('video').forEach((video) => {
          if (video.parentElement?.classList.contains('gl-article__video')) return;

          const list = video.closest('ul, ol');
          const paragraph = video.closest('p');
          const host = list || paragraph;

          video.removeAttribute('width');
          video.removeAttribute('height');
          video.setAttribute('playsinline', '');
          video.controls = true;

          const wrapper = document.createElement('figure');
          wrapper.className = 'gl-article__video';

          if (host && host.parentNode) {
            let insertAfter = host;
            let next = host.nextSibling;
            while (next && next.nodeType === 1 && next.classList.contains('gl-article__video')) {
              insertAfter = next;
              next = next.nextSibling;
            }
            host.parentNode.insertBefore(wrapper, insertAfter.nextSibling);
          } else if (video.parentNode) {
            video.parentNode.insertBefore(wrapper, video);
          }

          wrapper.appendChild(video);
        });

        this.content.querySelectorAll('table').forEach((table) => {
          if (table.parentElement?.classList.contains('gl-article__table-wrap')) return;
          const wrapper = document.createElement('div');
          wrapper.className = 'gl-article__table-wrap';
          table.parentNode.insertBefore(wrapper, table);
          wrapper.appendChild(table);
        });
      }

      initShare() {
        const shareRoots = this.querySelectorAll('[data-article-share]');
        if (!shareRoots.length) return;

        const sourceFor = (platform) => {
          if (platform === 'x') return 'twitter';
          if (platform === 'native') return navigator.share ? 'native_share' : 'copy_link';
          return platform;
        };

        const trackedUrl = (shareRoot, platform) => {
          const base = shareRoot.dataset.shareUrl || `${window.location.origin}${window.location.pathname}`;
          const url = new URL(base, window.location.origin);
          ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'].forEach((param) => {
            url.searchParams.delete(param);
          });
          url.searchParams.set('utm_source', sourceFor(platform));
          url.searchParams.set('utm_medium', 'social');
          url.searchParams.set('utm_campaign', 'article_share');
          url.searchParams.set(
            'utm_content',
            [shareRoot.dataset.shareContent, shareRoot.dataset.sharePlacement].filter(Boolean).join('_')
          );
          return url.toString();
        };

        shareRoots.forEach((shareRoot) => {
          const title = shareRoot.dataset.shareTitle || document.title;
          const copiedLabel = shareRoot.dataset.copiedLabel || 'Copied!';

          shareRoot.querySelectorAll('[data-share]').forEach((button) => {
            button.addEventListener('click', () => {
              const platform = button.dataset.share;
              const url = trackedUrl(shareRoot, platform);
              const encodedUrl = encodeURIComponent(url);
              const encodedTitle = encodeURIComponent(title);
              const shareLinks = {
                x: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
                facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
                reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
              };

              if (platform === 'native') {
                if (navigator.share) {
                  navigator.share({ title, url }).catch(() => {});
                } else {
                  navigator.clipboard.writeText(url).then(() => {
                    button.classList.add('is-copied');
                    const tooltip = button.querySelector('[data-copied]');
                    if (tooltip) tooltip.textContent = copiedLabel;
                    setTimeout(() => button.classList.remove('is-copied'), 2000);
                  });
                }
                return;
              }

              if (shareLinks[platform]) {
                window.open(shareLinks[platform], '_blank', 'width=600,height=400,noreferrer');
              }
            });
          });
        });
      }

      initRelatedProducts() {
        const root = this.querySelector('[data-related-products]');
        if (!root) return;

        const limit = Number(root.dataset.limit || 4);
        const filled = Number(root.dataset.filled || 0);
        if (filled >= limit) return;

        let tags = [];
        try {
          tags = JSON.parse(root.dataset.searchTags || '[]').filter((tag) => String(tag).trim());
        } catch (error) {
          return;
        }
        if (!tags.length) return;

        const grid = root.querySelector('[data-related-product-grid]');
        const productHref = (product) => {
          try {
            return new URL(product.url, window.location.origin).pathname;
          } catch (error) {
            return product.url;
          }
        };

        const existing = new Set(
          Array.from(grid?.querySelectorAll('[data-product-url]') || []).map((el) => {
            try {
              return new URL(el.getAttribute('href'), window.location.origin).pathname;
            } catch (error) {
              return el.getAttribute('href');
            }
          })
        );
        const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
        const tagSet = new Set(tags.map((tag) => tag.toLowerCase()));

        const productHasMatchingTag = (product) => {
          const productTags = Array.isArray(product.tags) ? product.tags : [];
          return productTags.some((tag) => tagSet.has(String(tag).toLowerCase()));
        };

        const fetchProducts = async (tag) => {
          const url = `/search/suggest.json?q=${encodeURIComponent(`tag:${tag}`)}&resources[type]=product&resources[limit]=${limit}&resources[options][unavailable_products]=hide`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) return [];
          const data = await response.json();
          return (data?.resources?.results?.products || []).filter(productHasMatchingTag);
        };

        const formatMoney = (value) => {
          const amount = Number(String(value).replace(/[^0-9.]/g, ''));
          if (Number.isNaN(amount)) return String(value);
          return moneyFormat.replace(/\{\{\s*amount(-with-comma-separator)?\s*\}\}/g, amount.toFixed(2));
        };

        const imageUrl = (product) => {
          const src = product.image || product.featured_image?.url || '';
          if (!src) return '';
          if (src.includes('{width}')) return src.replace('{width}', '600');
          return src;
        };

        const appendProduct = (product) => {
          if (!grid || existing.size >= limit) return;
          const href = productHref(product);
          if (!href || existing.has(href)) return;
          existing.add(href);

          const card = document.createElement('a');
          card.href = href;
          card.className = 'gl-related-card gl-related-card--product';
          card.dataset.productUrl = href;

          const imgSrc = imageUrl(product);
          if (imgSrc) {
            const media = document.createElement('div');
            media.className = 'gl-related-card__media';
            const img = document.createElement('img');
            img.src = imgSrc;
            img.alt = product.title || '';
            img.loading = 'lazy';
            media.appendChild(img);
            card.appendChild(media);
          }

          const title = document.createElement('h3');
          title.className = 'gl-related-card__title';
          title.textContent = product.title || '';
          card.appendChild(title);

          if (product.price) {
            const price = document.createElement('p');
            price.className = 'gl-related-card__price';
            price.textContent = formatMoney(product.price);
            card.appendChild(price);
          }

          grid.appendChild(card);
        };

        (async () => {
          for (const tag of tags) {
            if (existing.size >= limit) break;
            try {
              const products = await fetchProducts(tag);
              products.forEach(appendProduct);
            } catch (error) {
              // Ignore failed lookups and try the next tag.
            }
          }

          if (existing.size > 0) {
            root.hidden = false;
          } else {
            root.hidden = true;
            root.setAttribute('hidden', '');
          }
        })();
      }
    }
  );
}
